import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { clamp, K_MIN, K_MAX } from "../components/flow/flowGeometry";
import { reducer, inicial, DEBOUNCE_POS } from "../components/flow/flowReducer";

// Guardado del editor de flujo. El estado lo lleva flowReducer; aqui viven los
// efectos: el debounce de las posiciones y las mutaciones de estructura.
//
// Hay tres regimenes de guardado distintos, y cada uno tiene su motivo:
//
//   geometria     debounce de 600 ms y EN LOTE. Mover nodos no debe generar una
//                 peticion por nodo ni por frame. Es el mismo mecanismo que
//                 updateEntry en CuadernoPage, con sus dos refs.
//   estructura    inmediato y optimista (crear/borrar nodo o arista): se aplica
//                 en local, se lanza la peticion, y si falla se revierte. Es el
//                 patron de patchProperty en NotionProjectsPage.
//   campos        los lleva el panel de propiedades, no este hook.
//
// No hay boton de "Guardar" para el lienzo a proposito: en un lienzo, un
// guardado explicito significa perder el trabajo cuando alguien cierra la
// pestaña.

export function useFlowEditor({
  tareas, dependencias, editable,
  onGuardarPosiciones, onCrearPaso, onBorrarPaso,
  onCrearArista, onBorrarArista, onEditarArista, showToast,
}) {
  const [st, dispatch] = useReducer(reducer, inicial);
  const timerPos  = useRef(null);
  const pendientes = useRef({});   // ids con posicion pendiente de enviar

  // Cargar el grafo cuando llega o cambia
  useEffect(() => {
    dispatch({ type: "INIT", tareas, dependencias });
  }, [tareas, dependencias]);

  // ── Guardado de la geometria: debounce y en lote ───────────────────────────
  const enviarPosiciones = useCallback(async () => {
    const ids = Object.keys(pendientes.current);
    if (!ids.length) return;
    const nodos = ids.map(id => ({
      id,
      pos_x: pendientes.current[id].pos_x,
      pos_y: pendientes.current[id].pos_y,
    }));
    pendientes.current = {};
    const ok = await onGuardarPosiciones?.(nodos);
    if (ok !== false) dispatch({ type: "LIMPIAR_DIRTY", ids });
  }, [onGuardarPosiciones]);

  useEffect(() => {
    const ids = Object.keys(st.dirty);
    if (!ids.length || !editable) return;
    for (const id of ids) {
      const n = st.nodes[id];
      if (n) pendientes.current[id] = { pos_x: n.pos_x, pos_y: n.pos_y };
    }
    clearTimeout(timerPos.current);
    timerPos.current = setTimeout(enviarPosiciones, DEBOUNCE_POS);
    return () => clearTimeout(timerPos.current);
  }, [st.dirty, st.nodes, editable, enviarPosiciones]);

  // Al salir de la pagina se manda lo que quede pendiente. No es una garantia
  // (el navegador puede cortar), pero recupera el caso normal de cerrar la
  // pestaña justo despues de mover un nodo.
  useEffect(() => {
    const alSalir = () => {
      if (Object.keys(pendientes.current).length) {
        clearTimeout(timerPos.current);
        enviarPosiciones();
      }
    };
    window.addEventListener("beforeunload", alSalir);
    return () => { window.removeEventListener("beforeunload", alSalir); alSalir(); };
  }, [enviarPosiciones]);

  // ── Animacion del reorganizar ──────────────────────────────────────────────
  // Se interpola en JS y no con una transicion CSS porque la posicion del nodo
  // son atributos x/y del SVG, no un transform: transicionarlos no es fiable
  // entre motores. 320 ms con una curva suave: suficiente para seguir a donde
  // va cada nodo, corto para no estorbar.
  const animRaf = useRef(0);
  useEffect(() => {
    if (!st.anim || st.anim.t > 0) return;
    const DUR = 320;
    const t0 = performance.now();
    const suave = x => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
    const paso = () => {
      const p = Math.min(1, (performance.now() - t0) / DUR);
      if (p >= 1) { dispatch({ type: "ANIM_FIN" }); return; }
      dispatch({ type: "ANIM_PASO", t: suave(p) });
      animRaf.current = requestAnimationFrame(paso);
    };
    animRaf.current = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(animRaf.current);
  }, [st.anim]); // eslint-disable-line

  // A quien no puede editar nunca se le dice "Guardando…": el guardado esta
  // apagado para el, asi que el aviso se quedaria encendido para siempre.
  const guardando = editable && Object.keys(st.dirty).length > 0;

  // ── Estructura: optimista con vuelta atras ────────────────────────────────
  const crearPaso = useCallback(async (datos) => {
    const t = await onCrearPaso?.(datos);
    if (!t) return null;                        // el hook de datos ya ha avisado
    dispatch({ type: "ADD_NODE", node: t });
    return t;
  }, [onCrearPaso]);

  const borrarPaso = useCallback(async (id) => {
    const ok = await onBorrarPaso?.(id);
    if (ok) dispatch({ type: "DEL_NODE", id });
    return ok;
  }, [onBorrarPaso]);

  const crearArista = useCallback(async (datos) => {
    const e = await onCrearArista?.(datos);
    if (!e) return null;
    dispatch({ type: "ADD_EDGE", edge: e });
    // Un bucle no se bloquea: se avisa y se pinta distinto. Un retrabajo
    // ("revision -> corregir -> revision") es un flujo legitimo.
    if (e.cierra_ciclo)
      showToast?.("Esa conexion cierra un bucle. Se ha creado, pero la ruta critica no se podra calcular", "warn");
    return e;
  }, [onCrearArista, showToast]);

  const borrarArista = useCallback(async (id) => {
    const ok = await onBorrarArista?.(id);
    if (ok) dispatch({ type: "DEL_EDGE", id });
    return ok;
  }, [onBorrarArista]);

  const editarArista = useCallback(async (id, patch) => {
    dispatch({ type: "PATCH_EDGE", id, patch });     // reflejo inmediato
    const e = await onEditarArista?.(id, patch);
    if (e) dispatch({ type: "PATCH_EDGE", id, patch: e });
    return e;
  }, [onEditarArista]);

  // ── Deshacer y rehacer ────────────────────────────────────────────────────
  // No restauran una foto del estado: ejecutan el INVERSO de la ultima
  // operacion, y contra el servidor cuando hace falta. Con fotos, deshacer una
  // creacion dejaba el nodo vivo en la base de datos y volvia solo, y deshacer
  // un borrado dejaba un fantasma que el siguiente refresco se llevaba: eso es
  // lo que daba errores y perdia informacion.
  const deshacer = useCallback(async () => {
    const op = st.hist.past[st.hist.past.length - 1];
    if (!op) return;

    if (op.tipo === "mover") {
      const pos = {};
      for (const [id, p] of Object.entries(op.pos)) pos[id] = p.antes;
      dispatch({ type: "APLICAR_POS", pos });
      dispatch({ type: "HIST_DESHECHO" });
      return;
    }

    if (op.tipo === "crearNodo") {
      const ok = await onBorrarPaso?.(op.id);
      if (!ok) return;                          // el hook de datos ya ha avisado
      dispatch({ type: "DEL_NODE", id: op.id, local: true });
      dispatch({ type: "HIST_DESHECHO" });
      return;
    }

    if (op.tipo === "crearArista") {
      const ok = await onBorrarArista?.(op.id);
      if (!ok) return;
      dispatch({ type: "DEL_EDGE", id: op.id, local: true });
      dispatch({ type: "HIST_DESHECHO" });
      return;
    }

    if (op.tipo === "borrarArista") {
      const e = op.edge;
      const creada = await onCrearArista?.({
        from_task_id: e.from_task_id, to_task_id: e.to_task_id,
        from_handle: e.from_handle, to_handle: e.to_handle,
        tipo: e.tipo, etiqueta: e.etiqueta, condicion: e.condicion,
        opcional: e.opcional,
      });
      if (!creada) return;
      // Vuelve con OTRO id, que es lo normal: para la base de datos es una
      // conexion nueva. Lo que importa es que el flujo queda como estaba.
      dispatch({ type: "ADD_EDGE", edge: creada, local: true });
      dispatch({ type: "HIST_DESHECHO" });
    }
  }, [st.hist.past, onBorrarPaso, onBorrarArista, onCrearArista]);

  // Solo se rehacen los movimientos: rehacer una creacion daria un elemento con
  // otro id, y llamar a eso "rehacer" confundiria mas de lo que ayuda. Por eso
  // deshacer una operacion de estructura vacia la pila de rehacer.
  const rehacer = useCallback(() => {
    const op = st.hist.future[0];
    if (!op || op.tipo !== "mover") return;
    const pos = {};
    for (const [id, p] of Object.entries(op.pos)) pos[id] = p.despues;
    dispatch({ type: "APLICAR_POS", pos });
    dispatch({ type: "HIST_REHECHO" });
  }, [st.hist.future]);

  const nodoSel  = st.sel.nodes.length === 1 ? st.nodes[st.sel.nodes[0]] : null;
  const aristaSel = st.sel.edge ? st.edges[st.sel.edge] : null;

  // Aristas que salen y llegan a cada nodo, para el panel de conexiones.
  const conexiones = useMemo(() => {
    const m = {};
    for (const e of Object.values(st.edges)) {
      const f = String(e.from_task_id), t = String(e.to_task_id);
      (m[f] = m[f] || { salen: [], llegan: [] }).salen.push(e);
      (m[t] = m[t] || { salen: [], llegan: [] }).llegan.push(e);
    }
    return m;
  }, [st.edges]);

  return {
    st, dispatch,
    nodoSel, aristaSel, conexiones, guardando,
    crearPaso, borrarPaso, crearArista, borrarArista, editarArista,
    deshacer, rehacer,
    puedeDeshacer: st.hist.past.length > 0,
    // Solo hay algo que rehacer si es un movimiento (ver rehacer()).
    puedeRehacer:  st.hist.future[0]?.tipo === "mover",
  };
}

export { K_MIN, K_MAX, clamp };
